import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getPoojariById,
    updatePoojari,
    toggleAvailability,
    submitPoojaTypeProposal
} from '../../services/poojariService';
import { toPaise, paiseToRupees, formatPaise } from '../../utils/money';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import BankDetailsSection from '../../components/BankDetailsSection';
import FeeHistorySection from '../../components/FeeHistorySection';
import CalendarBlockSection from '../../components/CalendarBlockSection';
import UpcomingPoojasSection from '../../components/UpcomingPoojasSection';
import {
    fetchPoojaTypes,
    LANGUAGES,
    DURATION_OPTIONS,
    durationValue,
    formatDuration
} from '../../utils/poojaTypes';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

// ⚠️ Samagri par service charge — backend ke
//    yajman.samagri.service-charge-bps se MATCH hona chahiye
//    (wahan 300 bps = 3%).
//
//    Ye yahan hardcode hai kyunki naya service abhi save hua hi
//    nahi — uska quote backend se maang nahi sakte. Rate kabhi
//    badle to DONO jagah badalna hoga.
const SAMAGRI_CHARGE_PERCENT = 3;

/** Samagri price se "user kitna dega" nikaalo — sirf dikhane ke liye */
const withSamagriCharge = (rupees) => {
    const n = Number(rupees);
    if (!n || n <= 0) return 0;
    return Math.round(n * (100 + SAMAGRI_CHARGE_PERCENT) / 100);
};

const emptyService = {
    poojaType: '',
    price: '',
    durationMinutes: 60,
    durationDays: 1,
    samagriAvailable: false,
    samagriPrice: ''
};

const PoojariProfile2 = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toggling, setToggling] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        experienceYears: '',
        profileDescription: '',
        languagesKnown: [],
        isAvailable: true
    });

    const [services, setServices] = useState([]);
    const [newService, setNewService] = useState(emptyService);
    const [langSelect, setLangSelect] = useState('');
    const [poojaTypes, setPoojaTypes] = useState([]);
    const [otherOpen, setOtherOpen] = useState(false);
    const [otherName, setOtherName] = useState('');
    const [otherSending, setOtherSending] = useState(false);
    const [tab, setTab] = useState('profile');
    const [editSection, setEditSection] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [addingService, setAddingService] = useState(false);
    const [editingServiceIdx, setEditingServiceIdx] = useState(null);
    const [removeIdx, setRemoveIdx] = useState(null);

    const { token, user, updateUserData } = useAuth();
    const navigate = useNavigate();

    // Pooja types ek baar laao.
    // ⚠️ poojaTypes.js mein cache hai — Home aur Register se pehle
    //    aa chuki ho to dobara API hit nahi hogi.
    useEffect(() => {
        let alive = true;
        fetchPoojaTypes().then(list => {
            if (alive) setPoojaTypes(list);
        });
        return () => { alive = false; };
    }, []);
    useEffect(() => {
        if (!token) {
            navigate('/poojari-login');
            return;
        }
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const poojariId = user?.id;
            if (!poojariId) {
                toast.error('Session expired. Login again.');
                navigate('/poojari-login');
                return;
            }
            const data = await getPoojariById(poojariId);
            setProfile(data);
            resetForm(data);
        } catch (error) {
            toast.error('Could not load profile');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = (data) => {
        setFormData({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            pincode: data.pincode || '',
            experienceYears: data.experienceYears || '',
            profileDescription: data.profileDescription || '',
            languagesKnown: data.languagesKnown || [],
            isAvailable: data.isAvailable ?? true
        });
        // ⚠️ Backend PAISE bhejta hai (priceInPaise), par form mein
        // user RUPEES dekhta aur type karta hai. Isliye yahan
        // paise -> rupees, aur save karte waqt wapas rupees -> paise.
        setServices((data.services || []).map(s => ({
            poojaType: s.poojaType,
            price: paiseToRupees(s.priceInPaise),
            durationMinutes: s.durationMinutes,
            durationDays: s.durationDays || 1,
            samagriAvailable: s.samagriAvailable,
            samagriPrice: paiseToRupees(s.samagriPriceInPaise)
        })));
        setNewService(emptyService);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const onlyDigits = (name, value) => {
        setFormData({
            ...formData,
            [name]: value.replace(/\D/g, '')
        });
    };

    // ===== Services =====

    // ⚠️ Dropdown ka sentinel. Ye kabhi poojaType nahi ban sakta —
    //    @Pattern underscore allow nahi karta, aur yahan bhi guard
    //    hai.
    const OTHER_VALUE = '__OTHER__';

    const handleServiceChange = (e) => {
        const { name, value, type, checked } = e.target;

        // "Other" chuna — modal kholo, dropdown KHAALI rakho.
        //
        // ⚠️ Dropdown khaali rakhna zaroori hai. Warna __OTHER__
        //    newService.poojaType ban jaata, aur poojari Add daba
        //    deta to wahi string pooja ka naam ban jaati.
        if (name === 'poojaType' && value === OTHER_VALUE) {
            setOtherOpen(true);
            setNewService({ ...newService, poojaType: '' });
            return;
        }

        setNewService({
            ...newService,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    /**
     * Nayi pooja ka proposal bhejo.
     *
     * ⚠️ Proposal bhejne se pooja TURANT use nahi hoti — admin
     *    approve karega tab list mein aayegi. Ye baat UI mein saaf
     *    likhi hai, warna poojari samajhta ki add ho gaya.
     */
    const submitOther = async () => {
        const name = otherName.trim();

        if (name.length < 3) {
            return toast.warning('Enter a valid pooja name');
        }

        setOtherSending(true);
        try {
            await submitPoojaTypeProposal(name);
            toast.success(
                'Request sent. We will review it and let you '
                + 'know by email.');
            setOtherOpen(false);
            setOtherName('');
        } catch (error) {
            // ⚠️ Backend ka message hi dikhao — wo saaf hota hai
            //    ("This pooja is already in the list", waghairah).
            toast.error(error.response?.data?.message
                || 'Could not send request');
        } finally {
            setOtherSending(false);
        }
    };

    // ============================================================
    // SERVICES - view mode se add / edit / remove
    // ============================================================
    //
    // ⚠️ Ye teeno seedha SAVE karte hain - poora profile object
    //    backend ko jaata hai, wahi jo "Edit" form bhejta tha.
    //    Backend mein kuch nahi badla.
    //
    // ⚠️ Poojari ko sirf ek pooja ka form dikhta hai. Pehle use
    //    poora "Edit" kholna padta tha - naam, address, languages
    //    sab saamne aa jaate the sirf ek pooja jodne ke liye.

    /**
     * Services ki nayi list backend ko bhejo.
     *
     * ⚠️ Ye handleSave ki tarah hi payload banata hai - wahi
     *    rupees->paise conversion, wahi field naam. Do jagah alag
     *    tareeka rakhne se ek din ek jagah galti hogi.
     */
    const saveServices = async (nextServices) => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                experienceYears: parseInt(formData.experienceYears),
                // ⚠️ PAISE MEIN - form se rupees aate hain
                services: nextServices.map(s => ({
                    poojaType: s.poojaType,
                    priceInPaise: toPaise(s.price),
                    durationMinutes: parseInt(s.durationMinutes),
                    durationDays: parseInt(s.durationDays) || 1,
                    samagriAvailable: Boolean(s.samagriAvailable),
                    samagriPriceInPaise: s.samagriAvailable
                        ? toPaise(s.samagriPrice || 0) : 0
                }))
            };

            const updated = await updatePoojari(profile.id, payload);
            setProfile(updated);
            setServices(nextServices);
            return true;

        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Could not save. Please try again.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    /** Nayi pooja jodo aur turant save karo */
    const handleAddService = async () => {
        if (!newService.poojaType) {
            return toast.warning('Select a pooja');
        }
        if (!newService.price || Number(newService.price) <= 0) {
            return toast.warning('Enter a valid price');
        }
        if (services.some(s => s.poojaType === newService.poojaType)) {
            return toast.info('This pooja is already added');
        }
        if (newService.samagriAvailable
            && (!newService.samagriPrice
                || Number(newService.samagriPrice) <= 0)) {
            return toast.warning('Enter samagri price');
        }

        const next = [...services, {
            poojaType: newService.poojaType,
            price: parseFloat(newService.price),
            durationMinutes: parseInt(newService.durationMinutes),
            durationDays: parseInt(newService.durationDays) || 1,
            samagriAvailable: newService.samagriAvailable,
            samagriPrice: newService.samagriAvailable
                ? parseFloat(newService.samagriPrice) : 0
        }];

        const ok = await saveServices(next);
        if (ok) {
            setNewService(emptyService);
            setAddingService(false);
            toast.success('Pooja added');
        }
    };

    /** Ek service ke badlav save karo */
    const handleSaveService = async () => {
        const s = services[editingServiceIdx];

        if (!s.price || Number(s.price) <= 0) {
            return toast.warning('Enter a valid price');
        }
        if (s.samagriAvailable
            && (!s.samagriPrice || Number(s.samagriPrice) <= 0)) {
            return toast.warning('Enter samagri price');
        }

        const ok = await saveServices(services);
        if (ok) {
            setEditingServiceIdx(null);
            toast.success('Saved');
        }
    };

    /** Popup se confirm hone par service hatao */
    const handleRemoveService = async () => {
        // ⚠️ Aakhri service nahi hat sakti - bina service ke
        //    poojari search mein dikhega hi nahi, aur use pata bhi
        //    nahi chalega ki bookings kyun band ho gayin.
        if (services.length <= 1) {
            setRemoveIdx(null);
            return toast.warning(
                'You need at least one pooja. Add another first.');
        }

        const next = services.filter((_, idx) => idx !== removeIdx);
        const ok = await saveServices(next);
        if (ok) {
            setRemoveIdx(null);
            toast.success('Pooja removed');
        }
    };

    // Existing service ka price ya duration badlo
    const updateServiceField = (index, field, value) => {
        const updated = [...services];
        if (field === 'samagriAvailable') {
            updated[index] = {
                ...updated[index],
                samagriAvailable: value,
                samagriPrice: value
                    ? updated[index].samagriPrice : 0
            };
        } else {
            updated[index] = {
                ...updated[index],
                [field]: value
            };
        }
        setServices(updated);
    };

    // ===== Languages =====

    const handleLangSelect = (e) => {
        const value = e.target.value;
        setLangSelect('');
        if (!value) return;
        if (formData.languagesKnown.includes(value)) {
            return toast.info('Already added');
        }
        setFormData({
            ...formData,
            languagesKnown: [
                ...formData.languagesKnown, value]
        });
    };

    const removeLanguage = (i) => {
        setFormData({
            ...formData,
            languagesKnown: formData.languagesKnown
                .filter((_, idx) => idx !== i)
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (services.length === 0) {
            return toast.warning(
                'Add at least one pooja service');
        }
        if (formData.languagesKnown.length === 0) {
            return toast.warning('Add at least one language');
        }
        const invalid = services.find(
            s => !s.price || Number(s.price) <= 0);
        if (invalid) {
            return toast.warning(
                `Enter a valid price for ${invalid.poojaType}`);
        }
        if (formData.pincode
            && !/^\d{6}$/.test(formData.pincode)) {
            return toast.warning('Pincode must be 6 digits');
        }

        setSaving(true);
        try {
            const payload = {
                ...formData,
                experienceYears: parseInt(
                    formData.experienceYears),
                // ⚠️ PAISE MEIN BHEJNA ZAROORI HAI.
                // Form se rupees aate hain (user "500" type karta
                // hai), backend paise maangta hai (50000).
                //
                // ⚠️ Field ke naam bhi badle hain:
                //    price        -> priceInPaise
                //    samagriPrice -> samagriPriceInPaise
                //
                // Naam JAAN-BOOJH KE badla gaya. Purana naam rehta
                // aur 500 bhej dete, to backend usko 500 paise = ₹5
                // samajh leta - 100 guna galti, bina kisi error ke.
                services: services.map(s => ({
                    poojaType: s.poojaType,
                    priceInPaise: toPaise(s.price),
                    durationMinutes: parseInt(
                        s.durationMinutes),
                    durationDays: parseInt(s.durationDays) || 1,
                    samagriAvailable: Boolean(
                        s.samagriAvailable),
                    samagriPriceInPaise: s.samagriAvailable
                        ? toPaise(s.samagriPrice || 0) : 0
                }))
            };
            const updated = await updatePoojari(
                profile.id, payload);
            setProfile(updated);
            resetForm(updated);
            updateUserData({ ...updated, role: 'POOJARI' });
            toast.success('Profile updated');
            setEditing(false);
            window.scrollTo(0, 0);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleAvailability = async () => {
        setToggling(true);
        try {
            const updated = await toggleAvailability(
                profile.id);
            setProfile(updated);
            resetForm(updated);
            toast.success(updated.isAvailable
                ? 'You are now available for bookings'
                : 'You are now marked unavailable');
        } catch (error) {
            toast.error('Could not update availability');
        } finally {
            setToggling(false);
        }
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    if (!profile) return (
        <div className="text-center py-5">
            <h5 style={{ color: DARK_RED }}>
                Profile not found
            </h5>
        </div>
    );

    const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`
        .toUpperCase();

    const availablePoojas = poojaTypes.filter(
        p => !services.some(s => s.poojaType === p));

    const startingPrice = profile.services?.length
        // ⚠️ profile.services backend se aaya hai - priceInPaise
        ? Math.min(...profile.services.map(s => s.priceInPaise))
        : profile.chargePerPooja || 0;

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className={`pb-screen ${editing
                ? 'pb-has-action-bar' : ''}`}>

            {/* ⚠️ Naam aur avatar HATA DIYE - ab wo NAVBAR mein
                hain. Do jagah hone se ek hi baat do baar dikhti
                thi, aur ek screen mein 100px bekaar jaate.

                ⚠️ Par rating, poojas ki ginti aur STATUS badge
                rakhe hain - wo navbar mein nahi hain aur poojari
                ko dekhne chahiye. Khaas kar status: agar wo
                PENDING hai to use pata hona chahiye ki abhi
                bookings nahi aayengi.

                ⚠️ Ab red bar nahi - navbar pehle se red hai. Do
                red bar ek ke neeche ek bhaari lagti. */}
            <div style={{
                backgroundColor: 'white',
                borderBottom: `1px solid ${GOLD}`,
                padding: '10px 16px'
            }}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <p className="mb-0"
                        style={{
                            color: '#666',
                            fontSize: '12px'
                        }}>
                        📍 {profile.city} &middot; ⭐{' '}
                        {profile.rating > 0
                            ? profile.rating : 'New'} &middot;{' '}
                        {profile.totalPoojasCompleted || 0}{' '}
                        poojas
                    </p>
                    <span className="badge"
                        style={{
                            backgroundColor:
                                profile.status === 'APPROVED'
                                    ? '#EAF3DE' : '#FFF4E0',
                            color:
                                profile.status === 'APPROVED'
                                    ? '#27500A' : '#8A5300',
                            fontSize: '10px'
                        }}>
                        {profile.status}
                    </span>
                </div>
            </div>
            <div className="container"
                style={{ maxWidth: '560px' }}>
                <div className="py-3">

                    {/* ===== TABS =====

                        ⚠️ Poojari ko sirf wahi cheez dikhe jo wo
                        dekhna chahta hai. Pehle poora page ek saath
                        khulta tha - personal details, services, bank,
                        calendar - aur wo scroll mein kho jaata tha.

                        ⚠️ Money aur Calendar alag isliye hain ki wo
                        alag mizaaj ki cheezein hain: Profile "main
                        kaun hoon", Money "mera hisaab", Calendar "main
                        kab free hoon". */}
                    <div className="d-flex gap-2 mb-3">
                        {[
                            { key: 'profile', label: 'Profile' },
                            { key: 'money', label: 'Money' },
                            { key: 'calendar', label: 'Calendar' }
                        ].map(t => (
                            <button
                                key={t.key}
                                type="button"
                                className="btn btn-sm flex-fill fw-bold"
                                onClick={() => {
                                    setTab(t.key);
                                    // ⚠️ Tab badalte hi edit band karo.
                                    //    Warna poojari doosre tab pe
                                    //    jaake wapas aaye to adhoora
                                    //    bhara form mil jaata hai.
                                    setEditSection(null);
                                }}
                                style={{
                                    backgroundColor: tab === t.key
                                        ? DARK_RED : 'white',
                                    color: tab === t.key
                                        ? GOLD : DARK_RED,
                                    border: `1px solid ${DARK_RED}`,
                                    borderRadius: '8px'
                                }}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* ===== AVAILABILITY ===== */}
                    <div className="card mb-3"
                        style={{
                            borderRadius: '12px',
                            border: `1px solid ${GOLD}`
                        }}>
                        <div className="card-body p-3 d-flex justify-content-between align-items-center">
                            <div>
                                <p className="mb-0 fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '14px'
                                    }}>
                                    {profile.isAvailable
                                        ? '✅ Accepting bookings'
                                        : '❌ Not accepting'}
                                </p>
                                <small className="text-muted">
                                    {profile.isAvailable
                                        ? 'Devotees can book you'
                                        : 'You are hidden from search'}
                                </small>
                            </div>
                            <button
                                className="btn btn-sm fw-bold flex-shrink-0"
                                style={{
                                    backgroundColor:
                                        profile.isAvailable
                                            ? '#EFEFEF' : GOLD,
                                    color: DARK_RED,
                                    border: 'none'
                                }}
                                onClick={handleToggleAvailability}
                                disabled={toggling}>
                                {toggling
                                    ? '...'
                                    : profile.isAvailable
                                        ? 'Go offline'
                                        : 'Go online'}
                            </button>
                        </div>
                    </div>

                    {!editing ? (
                        /* ================= VIEW MODE ================= */
                        <>
                            {/* ⚠️ My Profile aur My services ab SIRF
                                Profile tab mein. Pehle ye har haal mein
                                dikhte the - Money ya Calendar khol ke
                                bhi poojari ko yahi milta tha. */}
                            {tab === 'profile' && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold mb-0"
                                            style={{ color: DARK_RED }}>
                                            My Profile
                                        </h6>
                                        <button
                                            className="btn btn-sm fw-bold"
                                            style={{
                                                backgroundColor: RED,
                                                color: GOLD,
                                                border: 'none'
                                            }}
                                            onClick={() => {
                                                setEditing(true);
                                                window.scrollTo(0, 0);
                                            }}>
                                            Edit
                                        </button>
                                    </div>

                                    {/* ⚠️ Details chhupe hue - "View details" se
                                khulte hain. Poojari ko rozana experience,
                                languages, address nahi dekhne hote.

                                ⚠️ Par ek jhalak dikhti hai - bilkul khaali
                                button se pata nahi chalta ki andar kya hai,
                                aur poojari use kabhi kholta hi nahi. */}
                                    <div className="d-flex justify-content-between align-items-center p-3"
                                        style={{
                                            backgroundColor: 'white',
                                            border: `1px solid ${GOLD}`,
                                            borderRadius: detailsOpen
                                                ? '12px 12px 0 0' : '12px',
                                            borderBottom: detailsOpen
                                                ? 'none' : `1px solid ${GOLD}`,
                                            marginBottom: detailsOpen ? 0 : '1rem'
                                        }}>
                                        <span className="text-muted"
                                            style={{ fontSize: '12px' }}>
                                            {profile.city}
                                            {profile.pincode && ` - ${profile.pincode}`}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm p-0 flex-shrink-0 ms-2"
                                            onClick={() => setDetailsOpen(!detailsOpen)}
                                            style={{
                                                color: DARK_RED,
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textDecoration: 'none'
                                            }}>
                                            {detailsOpen ? 'Hide' : 'View details'}
                                        </button>
                                    </div>
                                    {detailsOpen && (
                                        <div className="card mb-3"
                                            style={{
                                                borderRadius: '0 0 12px 12px',
                                                border: `1px solid ${GOLD}`,
                                                borderTop: 'none'
                                            }}>
                                            <div className="card-body p-3 pt-0">
                                                {[
                                                    {
                                                        label: 'Experience',
                                                        value: `${profile.experienceYears} years`
                                                    },
                                                    {
                                                        label: 'Starting from',
                                                        // ⚠️ startingPrice PAISE mein hai
                                                        value: formatPaise(startingPrice)
                                                    },
                                                    {
                                                        label: 'Languages',
                                                        value: profile.languagesKnown
                                                            ?.join(', ') || '-'
                                                    },
                                                    {
                                                        label: 'Phone',
                                                        value: profile.phoneNumber
                                                    },
                                                    {
                                                        label: 'Email',
                                                        value: profile.email
                                                    },
                                                    {
                                                        label: 'Address',
                                                        value: `${profile.address}, ${profile.city}, ${profile.state}${profile.pincode ? ' - ' + profile.pincode : ''}`
                                                    }
                                                ].map((row, i) => (
                                                    <div key={i}
                                                        className="d-flex justify-content-between py-2"
                                                        style={{
                                                            borderBottom:
                                                                '1px solid #eee',
                                                            fontSize: '13px'
                                                        }}>
                                                        <span className="text-muted flex-shrink-0 me-3">
                                                            {row.label}
                                                        </span>
                                                        <strong className="text-end"
                                                            style={{
                                                                color: DARK_RED,
                                                                wordBreak:
                                                                    'break-word'
                                                            }}>
                                                            {row.value}
                                                        </strong>
                                                    </div>
                                                ))}

                                                <div className="pt-3">
                                                    <small className="text-muted d-block mb-1">
                                                        About
                                                    </small>
                                                    <p className="mb-0"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        {profile.profileDescription
                                                            || 'No description added'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* ===== SERVICES =====

                                ⚠️ Add / edit / remove ab YAHIN hote hain,
                                "Edit" form mein nahi. Pehle poojari ko sirf
                                ek pooja jodni ho to bhi poora form kholna
                                padta tha - naam, address, languages sab
                                saamne aa jaate the.

                                ⚠️ "+ Add" UPAR hai, neeche nahi. 20 pooja
                                wale poojari ko neeche tak scroll karna
                                padta, aur wo shayad karta hi nahi. */}
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-bold"
                                            style={{ color: DARK_RED, fontSize: '14px' }}>
                                            My services
                                        </span>
                                        {!addingService && editingServiceIdx === null && (
                                            <button type="button"
                                                className="btn btn-sm p-0 fw-bold"
                                                onClick={() => setAddingService(true)}
                                                style={{
                                                    color: DARK_RED,
                                                    fontSize: '13px',
                                                    textDecoration: 'none'
                                                }}>
                                                + Add
                                            </button>
                                        )}
                                    </div>

                                    {/* ===== ADD FORM ===== */}
                                    {addingService && (
                                        <div className="p-3 mb-3"
                                            style={{
                                                border: `1px solid ${GOLD}`,
                                                borderRadius: '12px',
                                                backgroundColor: 'white'
                                            }}>
                                            <p className="fw-bold mb-3"
                                                style={{ color: DARK_RED, fontSize: '13px' }}>
                                                Add a pooja
                                            </p>

                                            <select className="form-select form-select-sm mb-2"
                                                name="poojaType"
                                                value={newService.poojaType}
                                                onChange={handleServiceChange}
                                                style={{ borderColor: GOLD }}>
                                                <option value="">Select pooja</option>
                                                {poojaTypes.map(p => (
                                                    <option key={p} value={p}>{p}</option>
                                                ))}
                                                <option value={OTHER_VALUE}>
                                                    Other - request a new pooja
                                                </option>
                                            </select>

                                            <div className="d-flex gap-2 mb-2">
                                                <input type="tel"
                                                    className="form-control form-control-sm"
                                                    name="price"
                                                    placeholder="Fees ₹"
                                                    inputMode="numeric"
                                                    value={newService.price}
                                                    onChange={handleServiceChange}
                                                    style={{ borderColor: GOLD }} />
                                                <select className="form-select form-select-sm"
                                                    value={durationValue(
                                                        newService.durationMinutes,
                                                        newService.durationDays)}
                                                    onChange={e => {
                                                        // ⚠️ Value "minutes_days" hai -
                                                        //    select ek hi value rakh sakta
                                                        //    hai, hamein do chahiye.
                                                        const [m, d] = e.target.value.split('_');
                                                        setNewService({
                                                            ...newService,
                                                            durationMinutes: parseInt(m),
                                                            durationDays: parseInt(d)
                                                        });
                                                    }}
                                                    style={{ borderColor: GOLD }}>
                                                    {DURATION_OPTIONS.map(o => (
                                                        <option key={o.label}
                                                            value={durationValue(o.minutes, o.days)}>
                                                            {o.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <label className="d-flex align-items-center gap-2 mb-2"
                                                style={{ fontSize: '13px' }}>
                                                <input type="checkbox"
                                                    name="samagriAvailable"
                                                    checked={newService.samagriAvailable}
                                                    onChange={handleServiceChange} />
                                                I can arrange samagri
                                            </label>

                                            {newService.samagriAvailable && (
                                                <>
                                                    <input type="tel"
                                                        className="form-control form-control-sm"
                                                        name="samagriPrice"
                                                        placeholder="Samagri ₹"
                                                        inputMode="numeric"
                                                        value={newService.samagriPrice}
                                                        onChange={handleServiceChange}
                                                        style={{ borderColor: GOLD }} />

                                                    {Number(newService.samagriPrice) > 0 && (
                                                        <div style={{
                                                            backgroundColor: '#FFF5F5',
                                                            borderRadius: '8px',
                                                            padding: '10px 12px',
                                                            marginTop: '8px',
                                                            fontSize: '12px'
                                                        }}>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span style={{ color: '#666' }}>
                                                                    You receive
                                                                </span>
                                                                <span>₹{Number(newService.samagriPrice)}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between">
                                                                <span style={{ color: '#666' }}>
                                                                    Service charge ({SAMAGRI_CHARGE_PERCENT}%)
                                                                </span>
                                                                <span>
                                                                    ₹{withSamagriCharge(newService.samagriPrice)
                                                                        - Number(newService.samagriPrice)}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex justify-content-between fw-bold"
                                                                style={{
                                                                    borderTop: '1px solid #E0D0D0',
                                                                    marginTop: '6px',
                                                                    paddingTop: '6px',
                                                                    color: DARK_RED
                                                                }}>
                                                                <span>User pays for samagri</span>
                                                                <span>₹{withSamagriCharge(newService.samagriPrice)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            <div className="d-flex gap-2 mt-3">
                                                <button type="button"
                                                    className="btn btn-sm flex-fill"
                                                    disabled={saving}
                                                    onClick={() => {
                                                        setAddingService(false);
                                                        setNewService(emptyService);
                                                    }}
                                                    style={{
                                                        border: '1px solid #DDD',
                                                        borderRadius: '8px',
                                                        fontSize: '13px'
                                                    }}>
                                                    Cancel
                                                </button>
                                                <button type="button"
                                                    className="btn btn-sm fw-bold"
                                                    disabled={saving}
                                                    onClick={handleAddService}
                                                    style={{
                                                        flex: 2,
                                                        backgroundColor: GOLD,
                                                        color: DARK_RED,
                                                        borderRadius: '8px',
                                                        fontSize: '13px'
                                                    }}>
                                                    {saving ? 'Adding...' : 'Add this pooja'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* ===== LIST ===== */}
                                    {!profile.services?.length ? (
                                        <p className="text-muted mb-4"
                                            style={{ fontSize: '13px' }}>
                                            No poojas added yet. Tap "+ Add" to add one.
                                        </p>
                                    ) : (
                                        <div className="mb-4">
                                            {profile.services.map((s, i) => (
                                                editingServiceIdx === i ? (
                                                    /* ===== EDIT FORM ===== */
                                                    <div key={i} className="p-3 mb-2"
                                                        style={{
                                                            border: `1px solid ${GOLD}`,
                                                            borderRadius: '12px',
                                                            backgroundColor: 'white'
                                                        }}>
                                                        {/* ⚠️ Pooja ka NAAM badal nahi sakte.
                                                    Naam badalne ka matlab hai wo alag
                                                    pooja hai - aur us par purani
                                                    bookings tik hui ho sakti hain. */}
                                                        <p className="fw-bold mb-3"
                                                            style={{ color: DARK_RED, fontSize: '13px' }}>
                                                            {s.poojaType}
                                                        </p>

                                                        <div className="d-flex gap-2 mb-2">
                                                            <input type="tel"
                                                                className="form-control form-control-sm"
                                                                placeholder="Fees ₹"
                                                                inputMode="numeric"
                                                                value={services[i]?.price || ''}
                                                                onChange={e => updateServiceField(
                                                                    i, 'price',
                                                                    e.target.value.replace(/\D/g, ''))}
                                                                style={{ borderColor: GOLD }} />
                                                            <select className="form-select form-select-sm"
                                                                value={durationValue(
                                                                    services[i]?.durationMinutes || 60,
                                                                    services[i]?.durationDays || 1)}
                                                                onChange={e => {
                                                                    // ⚠️ Value "minutes_days" hai -
                                                                    //    select ek hi value rakh
                                                                    //    sakta hai, hamein do
                                                                    //    chahiye.
                                                                    const [m, d] = e.target.value.split('_');
                                                                    updateServiceField(i, 'durationMinutes', m);
                                                                    updateServiceField(i, 'durationDays', d);
                                                                }}
                                                                style={{ borderColor: GOLD }}>
                                                                {DURATION_OPTIONS.map(o => (
                                                                    <option key={o.label}
                                                                        value={durationValue(o.minutes, o.days)}>
                                                                        {o.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <label className="d-flex align-items-center gap-2 mb-2"
                                                            style={{ fontSize: '13px' }}>
                                                            <input type="checkbox"
                                                                checked={!!services[i]?.samagriAvailable}
                                                                onChange={e => updateServiceField(
                                                                    i, 'samagriAvailable', e.target.checked)} />
                                                            I can arrange samagri
                                                        </label>

                                                        {services[i]?.samagriAvailable && (
                                                            <>
                                                                <input type="tel"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Samagri ₹"
                                                                    inputMode="numeric"
                                                                    value={services[i]?.samagriPrice || ''}
                                                                    onChange={e => updateServiceField(
                                                                        i, 'samagriPrice',
                                                                        e.target.value.replace(/\D/g, ''))}
                                                                    style={{ borderColor: GOLD }} />

                                                                {Number(services[i]?.samagriPrice) > 0 && (
                                                                    <div style={{
                                                                        backgroundColor: '#FFF5F5',
                                                                        borderRadius: '8px',
                                                                        padding: '10px 12px',
                                                                        marginTop: '8px',
                                                                        fontSize: '12px'
                                                                    }}>
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span style={{ color: '#666' }}>
                                                                                You receive
                                                                            </span>
                                                                            <span>₹{Number(services[i].samagriPrice)}</span>
                                                                        </div>
                                                                        <div className="d-flex justify-content-between">
                                                                            <span style={{ color: '#666' }}>
                                                                                Service charge ({SAMAGRI_CHARGE_PERCENT}%)
                                                                            </span>
                                                                            <span>
                                                                                ₹{withSamagriCharge(services[i].samagriPrice)
                                                                                    - Number(services[i].samagriPrice)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex justify-content-between fw-bold"
                                                                            style={{
                                                                                borderTop: '1px solid #E0D0D0',
                                                                                marginTop: '6px',
                                                                                paddingTop: '6px',
                                                                                color: DARK_RED
                                                                            }}>
                                                                            <span>User pays for samagri</span>
                                                                            <span>₹{withSamagriCharge(services[i].samagriPrice)}</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        <div className="d-flex gap-2 mt-3">
                                                            <button type="button"
                                                                className="btn btn-sm flex-fill"
                                                                disabled={saving}
                                                                onClick={() => {
                                                                    // ⚠️ Cancel pe purana data
                                                                    //    wapas - warna adhoore
                                                                    //    badlav state mein pade
                                                                    //    reh jaate hain.
                                                                    setServices(profile.services.map(x => ({
                                                                        poojaType: x.poojaType,
                                                                        price: paiseToRupees(x.priceInPaise),
                                                                        durationMinutes: x.durationMinutes,
                                                                        durationDays: x.durationDays || 1,
                                                                        samagriAvailable: x.samagriAvailable,
                                                                        samagriPrice: paiseToRupees(x.samagriPriceInPaise)
                                                                    })));
                                                                    setEditingServiceIdx(null);
                                                                }}
                                                                style={{
                                                                    border: '1px solid #DDD',
                                                                    borderRadius: '8px',
                                                                    fontSize: '13px'
                                                                }}>
                                                                Cancel
                                                            </button>
                                                            <button type="button"
                                                                className="btn btn-sm fw-bold"
                                                                disabled={saving}
                                                                onClick={handleSaveService}
                                                                style={{
                                                                    flex: 2,
                                                                    backgroundColor: DARK_RED,
                                                                    color: GOLD,
                                                                    borderRadius: '8px',
                                                                    fontSize: '13px'
                                                                }}>
                                                                {saving ? 'Saving...' : 'Save changes'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* ===== NORMAL ROW ===== */
                                                    <div key={i}
                                                        className="d-flex justify-content-between align-items-start p-3 mb-2"
                                                        style={{
                                                            border: `1px solid ${GOLD}`,
                                                            borderRadius: '12px',
                                                            backgroundColor: '#FFF8F8'
                                                        }}>
                                                        <div style={{ minWidth: 0 }}>
                                                            <p className="mb-0 fw-bold text-truncate"
                                                                style={{ color: DARK_RED, fontSize: '14px' }}>
                                                                {s.poojaType}
                                                            </p>
                                                            <p className="mb-0 text-muted"
                                                                style={{ fontSize: '12px' }}>
                                                                {formatDuration(s.durationMinutes, s.durationDays)}
                                                                {s.samagriAvailable
                                                                    ? ` · samagri +${formatPaise(s.samagriPriceInPaise)}`
                                                                    : ' · no samagri'}
                                                            </p>
                                                        </div>
                                                        <div className="text-end flex-shrink-0 ms-2">
                                                            <strong style={{ color: RED }}>
                                                                {formatPaise(s.priceInPaise)}
                                                            </strong>
                                                            <div style={{ fontSize: '11px', marginTop: '4px' }}>
                                                                <button type="button"
                                                                    className="btn btn-sm p-0 fw-bold"
                                                                    onClick={() => {
                                                                        setEditingServiceIdx(i);
                                                                        setAddingService(false);
                                                                    }}
                                                                    style={{
                                                                        color: DARK_RED,
                                                                        fontSize: '11px',
                                                                        textDecoration: 'none'
                                                                    }}>
                                                                    Edit
                                                                </button>
                                                                <span className="mx-2" style={{ color: '#DDD' }}>|</span>
                                                                <button type="button"
                                                                    className="btn btn-sm p-0"
                                                                    onClick={() => setRemoveIdx(i)}
                                                                    style={{
                                                                        color: '#C62828',
                                                                        fontSize: '11px',
                                                                        textDecoration: 'none'
                                                                    }}>
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {/* ⚠️ Ab ye apne-apne tab mein hain.
                                Pehle dono Profile ke neeche pade the -
                                poojari ko scroll karke dhoondhna padta
                                tha, aur bank details wo kabhi kholta
                                hi nahi tha. */}
                            {tab === 'calendar' && (
                                <>
                                    {/* ⚠️ Bookings PEHLE - poojari ko yahi
                                        dekhna hota hai. Blocked dates wo
                                        kabhi-kabhi hi jodta hai. */}
                                    <UpcomingPoojasSection poojariId={profile.id} />
                                    <CalendarBlockSection poojariId={profile.id} />
                                </>
                            )}

                            {tab === 'money' && (
                                <>
                                    <BankDetailsSection poojariId={profile.id} />
                                    {/* ⚠️ Bank details ke NEECHE - jaan-boojh
                                        ke. Poojari pehle ye pakka kare ki
                                        uska paisa kahan aayega, phir fee ka
                                        hisaab dekhe. */}
                                    <FeeHistorySection poojariId={profile.id} />
                                </>
                            )}
                        </>
                    ) : (
                        /* ================= EDIT MODE ================= */
                        <form onSubmit={handleSave}>

                            <h6 className="fw-bold mb-3"
                                style={{ color: DARK_RED }}>
                                Edit profile
                            </h6>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    First name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Last name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Address
                                </label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    rows="2"
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    City
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-7">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            borderColor: GOLD
                                        }}
                                    />
                                </div>
                                <div className="col-5">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Pincode
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        maxLength="6"
                                        inputMode="numeric"
                                        value={formData.pincode}
                                        onChange={e =>
                                            onlyDigits('pincode',
                                                e.target.value)}
                                        style={{
                                            borderColor: GOLD
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-5">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Experience
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        maxLength="2"
                                        inputMode="numeric"
                                        value={
                                            formData.experienceYears}
                                        onChange={e =>
                                            onlyDigits(
                                                'experienceYears',
                                                e.target.value)}
                                        required
                                        style={{
                                            borderColor: GOLD
                                        }}
                                    />
                                </div>
                                <div className="col-7">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Languages
                                    </label>
                                    <select
                                        className="form-select"
                                        value={langSelect}
                                        onChange={handleLangSelect}
                                        style={{
                                            borderColor: GOLD
                                        }}>
                                        <option value="">
                                            Add a language
                                        </option>
                                        {LANGUAGES.map(l => (
                                            <option key={l}
                                                value={l}>
                                                {l}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {formData.languagesKnown.length > 0 && (
                                <div className="mb-4">
                                    {formData.languagesKnown.map(
                                        (lang, i) => (
                                            <span key={i}
                                                className="badge bg-secondary me-2 mb-1 p-2">
                                                {lang}
                                                <button
                                                    type="button"
                                                    className="btn-close btn-close-white ms-2"
                                                    style={{
                                                        fontSize: '0.6rem'
                                                    }}
                                                    onClick={() =>
                                                        removeLanguage(i)}>
                                                </button>
                                            </span>
                                        ))}
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    About you
                                </label>
                                <textarea
                                    className="form-control"
                                    name="profileDescription"
                                    rows="3"
                                    value={
                                        formData.profileDescription}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* ===== STICKY ACTION BAR - edit mode ===== */}
            {editing && (
                <div className="pb-action-bar">
                    <button
                        className="btn btn-secondary flex-fill"
                        onClick={() => {
                            resetForm(profile);
                            setEditing(false);
                            window.scrollTo(0, 0);
                        }}
                        disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="btn flex-fill fw-bold"
                        style={{
                            backgroundColor: RED,
                            color: GOLD,
                            border: 'none'
                        }}
                        onClick={handleSave}
                        disabled={saving}>
                        {saving ? 'Saving...' : 'Save changes'}
                    </button>
                </div>
            )}
            {/* ===== REMOVE POPUP =====
                                ⚠️ Popup, line-wala confirm nahi: "Edit"
                                aur "Remove" bagal-bagal hain aur mobile
                                pe ungli phisal sakti hai. Service chali
                                gayi to wapas laane ka koi raasta nahi -
                                poojari ko dobara bharni padegi. */}
            {removeIdx !== null && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    zIndex: 1050
                }}
                    onClick={() => setRemoveIdx(null)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '14px',
                            padding: '20px',
                            maxWidth: '340px',
                            width: '100%'
                        }}>
                        <p className="fw-bold mb-2"
                            style={{ color: DARK_RED, fontSize: '15px' }}>
                            Remove {profile.services?.[removeIdx]?.poojaType}?
                        </p>
                        <p className="text-muted mb-3"
                            style={{ fontSize: '13px' }}>
                            Devotees will not be able to book this pooja.
                        </p>
                        <div className="d-flex gap-2">
                            <button type="button"
                                className="btn btn-sm flex-fill"
                                disabled={saving}
                                onClick={() => setRemoveIdx(null)}
                                style={{
                                    border: '1px solid #DDD',
                                    borderRadius: '8px',
                                    fontSize: '13px'
                                }}>
                                Keep it
                            </button>
                            <button type="button"
                                className="btn btn-sm flex-fill fw-bold"
                                disabled={saving}
                                onClick={handleRemoveService}
                                style={{
                                    backgroundColor: '#C62828',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontSize: '13px'
                                }}>
                                {saving ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== NAYI POOJA TYPE KI MAANG ===== */}
            {otherOpen && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    zIndex: 1050
                }}
                    onClick={() => setOtherOpen(false)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '14px',
                            padding: '20px',
                            maxWidth: '340px',
                            width: '100%'
                        }}>
                        <p className="fw-bold mb-2"
                            style={{ color: DARK_RED, fontSize: '15px' }}>
                            Request a new pooja
                        </p>
                        <p className="text-muted mb-3"
                            style={{ fontSize: '13px' }}>
                            We will review it and add it to the list.
                            You will be able to add it after that.
                        </p>

                        <input type="text"
                            className="form-control form-control-sm"
                            placeholder="Pooja name"
                            value={otherName}
                            onChange={e => setOtherName(e.target.value)}
                            style={{ borderColor: GOLD }} />

                        <div className="d-flex gap-2 mt-3">
                            <button type="button"
                                className="btn btn-sm flex-fill"
                                disabled={otherSending}
                                onClick={() => {
                                    setOtherOpen(false);
                                    setOtherName('');
                                }}
                                style={{
                                    border: '1px solid #DDD',
                                    borderRadius: '8px',
                                    fontSize: '13px'
                                }}>
                                Cancel
                            </button>
                            <button type="button"
                                className="btn btn-sm flex-fill fw-bold"
                                disabled={otherSending}
                                onClick={submitOther}
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '8px',
                                    fontSize: '13px'
                                }}>
                                {otherSending ? 'Sending...' : 'Send request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PoojariProfile2;